import React, { FC, useMemo } from 'react'

import clsx from 'clsx'
import { FieldErrors, UseFormRegister, UseFormWatch } from 'react-hook-form'
import { UseFormSetValue } from 'react-hook-form/dist/types/form'

import { Tooltip, Txt } from 'src/components/atoms'
import { TextInput } from 'src/components/atoms/inputs/TextInput'
import { FormItemTitle } from 'src/components/molecules'
import { ProjectEmailDisplayNameTooltipContent } from 'src/components/molecules/projects/ProjectEmailDisplayNameTooltipContent'

import { EmailDisplayNameMaxLength } from 'src/constants'
import { UpsertProjectFormEntity } from 'src/types'

type Props = {
  register: UseFormRegister<UpsertProjectFormEntity>
  watch: UseFormWatch<UpsertProjectFormEntity>
  setValue: UseFormSetValue<UpsertProjectFormEntity>
  formErrors: FieldErrors<UpsertProjectFormEntity>
  className?: string
}

export const ProjectEmailDisplayNameInputArea: FC<Props> = ({
  register,
  watch,
  setValue,
  formErrors,
  className,
}) => {
  const watchedEmailDisplayName = watch('emailDisplayName')
  /** 現在の入力値の文字数 */
  const currentLength = watchedEmailDisplayName ? watchedEmailDisplayName.length : 0

  /**
   * 入力されている文字数に応じた状態
   * - normal・・文字数 < 制限値*90%
   * - warn・・制限値*90% <= 文字数 < 制限値
   * - danger・・文字数 >= 制限値
   * */
  const characterCountState = useMemo(() => {
    const warnThreshold = Math.floor(EmailDisplayNameMaxLength * 0.9)

    if (currentLength >= EmailDisplayNameMaxLength) {
      return 'danger'
    }
    if (currentLength >= warnThreshold) {
      return 'warn'
    }
    return 'normal'
  }, [currentLength])

  return (
    <div className={className}>
      <div className="tw-bg-surface-secondary tw-p-s1 tw-rounded-[8px]">
        <div className="tw-grid tw-gap-s-4">
          <div className="tw-full-justify">
            <Tooltip content={<ProjectEmailDisplayNameTooltipContent />} placement="top">
              <FormItemTitle text="送信元名" />
            </Tooltip>

            <Txt
              className={clsx(
                // 入力されている文字数に応じて色を変える
                characterCountState === 'normal' && 'tw-text-standardText-secondary',
                characterCountState === 'warn' && 'tw-text-cautionYellow-default',
                characterCountState === 'danger' && 'tw-text-errorRed-default'
              )}
            >
              {currentLength}/{EmailDisplayNameMaxLength}
            </Txt>
          </div>

          <TextInput
            placeholder="◯△株式会社"
            hasError={!!formErrors.emailDisplayName}
            // onChange で文字数制限をかけるがinputの基本機能でも念のため制限をかける
            maxLength={EmailDisplayNameMaxLength}
            {...register('emailDisplayName')}
            // maxLengthのみだと、日本語入力時に最大文字数以上入力出来ているような表示になるため、onChange で制限をかける
            onChange={event => {
              const input = event.target.value
              // 文字数制限を超えた場合は制限内の文字列に切り詰める
              const newValue =
                input.length > EmailDisplayNameMaxLength
                  ? input.slice(0, EmailDisplayNameMaxLength)
                  : input
              setValue('emailDisplayName', newValue)
            }}
            className="tw-w-full tw-bg-surface-primary"
          />
        </div>
      </div>
    </div>
  )
}
